import { factor } from '@fameacc/money';
import { z } from 'zod';
import { centAmount, isoDate } from './posting.js';
/**
 * `GET /api/v1/berichte/startkennzahlen` — Auftrag `V-startkennzahlen`
 * (Zweig `v-startkennzahlen`, 2026-08-27), Auftrag des Auftraggebers mit
 * Bildschirmfoto des Altsystems: Kennzahlen-Karten auf der Startseite —
 * Umsatz Netto/Steuer/Brutto und Offene Forderungen, je Jahr, laufendes
 * Quartal und laufender Monat.
 *
 * **Warum das NICHT gegen `docs/superpowers/specs/`s „Kein Bericht vor dem
 * Export" verstösst, obwohl `UebersichtPage.tsx`s Kopfkommentar (Spec 8,
 * `DA-3`) genau das für die Übersicht ausdrücklich ablehnt.** Jener Text ist
 * eine Entscheidung vom 2026-08-19ff. gegen eine SELBST erfundene KPI-Fläche
 * ohne Auftrag. Dieser Auftrag ist der Auftraggeber selbst, mit einem
 * konkreten Bildschirmfoto — die Northstar-Regel „Ask-Runde nur, wenn wirklich
 * nur der Auftraggeber entscheiden kann" ist hier bereits VOM Auftraggeber
 * entschieden. Der Kopfkommentar von `UebersichtPage.tsx` wird in diesem
 * Auftrag mitgezogen, damit er nicht mehr das Gegenteil behauptet.
 *
 * **Zeitbasis ist das Rechnungsdatum (`issue_date`), nie das Buchungs- oder
 * Anlagedatum.** Wie beim Lückenbericht (`gaps.report.ts`) ist das die
 * einzige Grösse, die ein Steuerberater nachvollzieht.
 *
 * **Storno-Neutralität, ohne eine zweite Spalte zu erfinden.** Ein Storno
 * (`type = 'cancellation'`) trägt laut `document.service.ts`s Kopfkommentar
 * VORZEICHENGLEICHE Beträge zu seinem Ursprung — „gleiche Beträge, gleiche
 * Sätze" — und bucht die Umkehrung über die Journalbuchung, nicht über ein
 * negatives Vorzeichen auf dem eigenen Beleg. Der Ursprung wechselt beim
 * Ausstellen des Stornos automatisch (Trigger `apply_document_cancellation`,
 * Migration `0025`) von `status = 'issued'` auf `'cancelled'`. Die Summen
 * dieses Berichts filtern deshalb auf `status = 'issued'` UND auf die
 * werthaltigen Rechnungsarten — ein stornierter Ursprung fällt allein durch
 * seinen Status heraus, der Storno selbst fällt durch die Typliste heraus
 * (er steht nicht darin). Eine Gutschrift (`credit_note`, echte
 * Teilkorrektur, bleibt `issued`) wird VON der Summe ABGEZOGEN — sie trägt
 * eigene, nicht-negative Beträge (`document_totals_non_negative`), die erst
 * dieser Bericht mit umgekehrtem Vorzeichen einrechnet.
 *
 * **Nachtrag `K-umsatzaltbelege` (2026-09-08, `E-1062`/`E-1063`): gebuchte
 * Altrechnungen aus dem Altimport zählen mit, die Archivklasse nicht.**
 * `legacy_invoice`/`legacy_credit_note` stehen in keiner Zeile von
 * `document_type_range` (die Archivklasse bucht laut deren eigenem
 * Kopfkommentar planmässig nicht, `D-1`) und fielen deshalb aus der
 * werthaltigen Typliste — auch für die 2026er Belege, die der Altimport seit
 * `AI-2026` sehr wohl mit Forderung an Erlös bucht. Die Grenze ist seither
 * nicht mehr allein der Typ, sondern zusätzlich `document.journal_entry_id
 * IS NOT NULL` — dieselbe Spalte, mit der `beleg_offener_rest.forderung_gebucht`
 * (Migration `0174`) Archivklasse und gebuchte Klasse für die offenen
 * Forderungen trennt. `legacy_credit_note` zieht wie `credit_note` ab,
 * sobald gebucht; `legacy_cancellation` bleibt wie `cancellation` aussen vor
 * — sein Ursprung wechselt beim Einspielen ebenso auf `status = 'cancelled'`
 * (`AltimportService.vermerkeStorno()`), dieselbe Storno-Neutralität wie oben.
 *
 * **Offene Forderungen sind ehrlich benannt, nicht der 1400-Saldo.** Seit
 * Stufe C (Buchungspfad der Kontierung, `C-kontbuchung`) und dem Umbau
 * `W-kennzahlumbau` (2026-08-29) rechnet dieser Bericht über
 * `OffenePostenQuery.offeneForderungenFuer()` denselben exakten
 * Rechnungsstand (Forderung minus Zahlungen, aus dem Journal), den auch
 * `GET /berichte/offene-posten` zeigt — eine Teilzahlung schlägt hier durch,
 * anders als früher, als `bank_transaction.match_status = 'matched'` binär
 * über „ganz oder gar nicht zugeordnet" entschied. Es ist trotzdem NICHT der
 * volle 1400-Saldo: `nichtZugeordnet` (eine kontierte Zahlung ohne
 * Belegzuordnung, eine Alt-Zahlung ohne Buchung) trägt kein `issue_date` und
 * lässt sich keinem Zeitraum zuordnen — diese Zahl bleibt der
 * Offene-Posten-Bericht selbst, zu dem die Startseiten-Karte verlinkt.
 * `grenze` benennt das im Bericht selbst, damit die Zahl nicht als der volle
 * Sachkontosaldo missverstanden wird.
 */
export const startkennzahlenZeitraumSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    netTotal: centAmount,
    taxTotal: centAmount,
    grossTotal: centAmount,
    offeneForderungenSumme: centAmount,
    offeneForderungenAnzahl: z.number().int().nonnegative(),
})
    .strict();
export const startkennzahlenBerichtSchema = z
    .object({
    stichtag: isoDate,
    jahr: startkennzahlenZeitraumSchema,
    quartal: startkennzahlenZeitraumSchema,
    monat: startkennzahlenZeitraumSchema,
    /**
     * `true`, wenn im Jahreszeitraum (der Quartal und Monat als Teilmengen
     * einschliesst) ausschliesslich der Satz 19 % vorkommt — die Bedingung,
     * unter der die Oberfläche die Steuerkarte als „Umsatzsteuer (MwSt 19 %)"
     * statt neutral als „Umsatzsteuer" beschriften darf.
     */
    nurNeunzehnProzent: z.boolean(),
    /** Die benannte Grenze der Offene-Forderungen-Kennzahl, für den Bericht selbst. */
    grenze: z.string(),
})
    .strict();
/**
 * `GET /api/v1/berichte/dashboard-kennzahlen` — Auftrag `B-dashagg`
 * (Zweig `b-dashagg`, 2026-08-31), abgeleitet aus dem Design-Scribble
 * `docs/scribbles/2026-08-31-dashboard-design.md` (Abschnitt 4.1) für den
 * geplanten Dashboard-Umbau der Startseite.
 *
 * **EINE Aggregation statt vier bis fünf getrennter Abfragen, mit
 * Begründung im Scribble selbst.** Chart-Balken, Kategorie-Aufschlüsselung,
 * Trend-Prozent und die KPI-Karten „Ausgaben"/„Ergebnis" ziehen alle aus
 * demselben Journal-/Dokumentenbestand über denselben Zeitraum — ein
 * getrennter Endpunkt je Karte hiesse denselben Bestand mehrfach zu lesen
 * und riskierte genau die Fehlerklasse, die `startkennzahlen.report.ts`
 * bereits einmal vermieden hat (zwei Ableitungen derselben Zahl laufen
 * auseinander).
 *
 * **Umsatz ist DIESELBE Ableitung wie `GET /berichte/startkennzahlen`, nicht
 * eine zweite.** Beide rufen `summeUmsatzUndGutschrift()`
 * (`startkennzahlen.report.ts`) mit derselben Datumsspanne auf — dieselben
 * werthaltigen Rechnungsarten (`invoice`, `partial_invoice`,
 * `final_invoice`, `recurring_invoice`, minus `credit_note`), derselbe
 * Status- und Typfilter für Storno-Neutralität, dasselbe Zeitmass
 * (`issue_date`). Eine Drift zwischen den beiden Endpunkten ist damit
 * strukturell ausgeschlossen, nicht nur beabsichtigt.
 *
 * **Ausgaben sind eine ECHTE Neuentwicklung und laufen über das
 * Buchungsdatum, nicht das Rechnungsdatum.** Summe von Soll minus Haben
 * über alle Buchungszeilen auf Konten der Art `expense`
 * (`account.type = 'expense'`), im Buchungsdatumsfenster (`booking_date`).
 * Zwei verschiedene Zeitbasen in einem Bericht — das ist bewusst und nicht
 * versehentlich: Ausgangsrechnungen zählen zum Zeitpunkt ihrer Ausstellung,
 * Ausgaben zum Zeitpunkt ihrer Buchung; beide sind die jeweils fachlich
 * richtige Grösse für ihre Seite, keine ist eine Annäherung an die andere.
 *
 * **„Ergebnis" heisst `einnahmenAusgabenSaldo` und NICHT `ebit`.** Es ist
 * die reine Differenz Umsatz minus Ausgaben, IST-Zahlen aus Beleg und
 * Journal — keine Abgrenzung, keine Rückstellung, kein testiertes
 * betriebswirtschaftliches Ergebnis. Der Name sagt das, damit niemand ihn
 * mit einer Grösse verwechselt, die dieses System nicht liefert.
 *
 * **Trend-% vergleicht mit der unmittelbar vorangehenden, gleich langen
 * Periode** (voriger Monat bei `zeitraum=monat`, voriges Quartal bei
 * `zeitraum=quartal` usw.), nie mit dem Vorjahr — ein Betrieb, der 2026
 * begonnen hat, hätte für die ersten Monate keine Vorjahresperiode. Ist der
 * Vergleichswert der Vorperiode exakt null, gibt es **keinen** Trendwert
 * (`null`, nie `0` und nie eine erfundene 100-%-Marke): Eine
 * Prozentänderung gegenüber null ist keine Zahl, die etwas behauptet, das
 * stimmt. Gerechnet wird ausschliesslich über `Factor`/`div()` aus
 * `packages/money` — eine Prozent-Differenz ist eine Geldableitung und
 * unterliegt Hausregel 1 genauso wie ein Betrag selbst.
 *
 * **Die Monatsreihe zeigt immer die letzten acht Kalendermonate, endend im
 * laufenden Monat — unabhängig vom gewählten `zeitraum`.** Der Chart im
 * Entwurf zeigt eine feste Zeitachse, keine Ableitung des Periodenschalters.
 * Ihr letzter Eintrag (der laufende Kalendermonat) liest dieselbe Datumsspanne
 * wie `periode`, wenn `zeitraum=monat` gewählt ist — das ist die geprüfte
 * Selbstkonsistenz zwischen Chart und KPI-Karte (`E-568`).
 *
 * **Ausgaben nach Kategorie: Top-`N` Aufwandskonten plus ein ehrlicher
 * Rest-Sammelposten**, nie eine abgeschnittene Liste ohne Hinweis auf das,
 * was fehlt — `rest`/`restAnzahlKonten` benennen genau das.
 *
 * **Nachtrag `W-uebersichtgesamt` (2026-09-10, `E-1115`): fünfter Zeitraum
 * `'gesamt'`, additiv.** Auftraggeberwunsch — neben Woche/Monat/Quartal/Jahr
 * auch „insgesamt" auswählbar. `'gesamt'` hat KEINE untere Grenze im
 * fachlichen Sinn (ab dem ersten Beleg/der ersten Buchung); technisch läuft
 * die Spanne ab `JOURNALBEGINN` (`apps/api/src/cutover/kalender.ts`,
 * `2026-01-01`) — vor diesem Tag trägt weder das Journal noch die gebuchte
 * Beleg-Klasse (`document.journal_entry_id IS NOT NULL`, siehe
 * `startkennzahlen.report.ts`) eine einzige Zeile, ein früheres Datum änderte
 * das Ergebnis also nicht, wäre aber eine erfundene Genauigkeit.
 *
 * **Für `'gesamt'` gibt es keine Vorperiode, und das ist keine Lücke,
 * sondern eine fachliche Tatsache.** Ein „voriger Gesamtzeitraum" wäre eine
 * Periode vor dem eigenen Anfang — es gibt keine, mit der man vergleichen
 * könnte. `periode.vorperiode` ist deshalb `null` (statt eines erfundenen
 * Zeitraums), und alle drei `trend*`-Felder sind `null` — dieselbe Regel wie
 * bei einer Vorperiode von exakt null (`trend()` in
 * `dashboard-kennzahlen.report.ts`), nur aus einem anderen Grund: dort gibt
 * es die Vorperiode, aber ihr Wert ist null; hier gibt es die Vorperiode
 * selbst nicht. Die Oberfläche zeigt für `'gesamt'` deshalb keine
 * Trend-Pille und keine „Vorperiode …"-Angabe in der Frischezeile
 * (`UebersichtPage.tsx`) — nie eine `0 %` und nie einen Gedankenstrich, der
 * einen Vergleich vortäuschte, den es nicht gibt.
 *
 * **Welche Kennzahlen für `'gesamt'` einen Sinn ergeben, und warum alle
 * es tun.** `umsatzNetto` und `ausgaben` sind FLÜSSE (Summen über einen
 * Zeitraum) und für `'gesamt'` die Summe seit dem Journalbeginn — genau das,
 * was „insgesamt" fachlich bedeutet. `einnahmenAusgabenSaldo` ist ihre
 * Differenz und trägt dieselbe Bedeutung. Die Monatsreihe und die
 * Ausgaben-nach-Kategorie-Aufschlüsselung sind unverändert: Erstere zeigt
 * ohnehin IMMER die letzten acht Kalendermonate, unabhängig vom gewählten
 * `zeitraum` (siehe deren eigener Absatz oben); letztere liest dieselbe
 * `spanne` wie `periode` und zeigt für `'gesamt'` folgerichtig die
 * Aufwandskonten seit dem Journalbeginn. Ein BESTAND (z. B. „offene Posten
 * heute") wäre für `'gesamt'` keine andere Zahl als für `'jahr'` — dieser
 * Bericht führt aber ohnehin keinen solchen Bestand, nur Flüsse, die Frage
 * stellt sich hier nicht.
 */
export const dashboardZeitraumTypSchema = z.enum(['woche', 'monat', 'quartal', 'jahr', 'gesamt']);
/**
 * Ein Trend als `Factor`: `0.05` heisst plus 5 %, `-0.12` heisst minus 12 %.
 *
 * Wie `taxRateFactor` (`documents.ts`) über `factor()` aus
 * `@fameacc/money` geprüft, aber ohne dessen Bereichsgrenze `< 1` —
 * ein Trend darf negativ sein (Rückgang) und über `1` liegen (mehr als
 * Verdopplung), beides kommt vor.
 */
export const dashboardTrendFactorSchema = z.string().transform((wert, ctx) => {
    try {
        return factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Trendfaktor' });
        return z.NEVER;
    }
});
export const dashboardPeriodeSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    umsatzNetto: centAmount,
    ausgaben: centAmount,
    einnahmenAusgabenSaldo: centAmount,
    /**
     * `null` für `zeitraum=gesamt` — es gibt keinen „vorigen
     * Gesamtzeitraum", mit dem man vergleichen könnte (siehe Kopfkommentar
     * von `dashboardZeitraumTypSchema` oben, Nachtrag `W-uebersichtgesamt`).
     */
    vorperiode: z.object({ von: isoDate, bis: isoDate }).strict().nullable(),
    /**
     * `null`, wenn die Vorperiode für diese Grösse exakt null war, ODER wenn
     * es (bei `zeitraum=gesamt`) gar keine Vorperiode gibt.
     */
    trendUmsatzNetto: dashboardTrendFactorSchema.nullable(),
    trendAusgaben: dashboardTrendFactorSchema.nullable(),
    trendEinnahmenAusgabenSaldo: dashboardTrendFactorSchema.nullable(),
})
    .strict();
export const dashboardMonatSchema = z
    .object({
    jahr: z.number().int(),
    monat: z.number().int().min(1).max(12),
    von: isoDate,
    bis: isoDate,
    umsatzNetto: centAmount,
    ausgaben: centAmount,
    einnahmenAusgabenSaldo: centAmount,
})
    .strict();
export const dashboardAusgabenKategorieSchema = z
    .object({
    /** Kontonummer (`account.number`), nicht die versionierte `id`. */
    konto: z.string(),
    name: z.string(),
    betrag: centAmount,
})
    .strict();
export const dashboardAusgabenNachKategorieSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    /** Die umsatzstärksten Aufwandskonten, absteigend, höchstens `TOP_N`. */
    kategorien: z.array(dashboardAusgabenKategorieSchema),
    /** Summe aller Aufwandskonten ausserhalb von `kategorien`. */
    rest: centAmount,
    /** Anzahl der Konten, die in `rest` zusammengefasst sind. */
    restAnzahlKonten: z.number().int().nonnegative(),
})
    .strict();
export const dashboardKennzahlenBerichtSchema = z
    .object({
    /** Zeitpunkt der Berechnung, ISO-8601 mit Offset — keine Cache-Zusage, nur Ehrlichkeit über das Alter. */
    stand: z.string().datetime({ offset: true }),
    zeitraum: dashboardZeitraumTypSchema,
    periode: dashboardPeriodeSchema,
    monatsreihe: z.array(dashboardMonatSchema),
    ausgabenNachKategorie: dashboardAusgabenNachKategorieSchema,
    grenze: z.string(),
})
    .strict();
//# sourceMappingURL=berichte.js.map